import { useEffect, useState } from "react";
import { api } from "../api/apiClient";
import { shopModeApi } from "../api/shopMode";
import type {
  AppLanguage,
  SalePeriodTotals,
  CourierPayoutRuleView,
  MeResponse,
  PayrollAdjustmentsResponse,
  Sale,
  Shift,
  MyWorkDayEarnings,
} from "../api/types";
import { APP_LANGUAGE_OPTIONS } from "../i18n/languages";
import { ruleSummary } from "../utils/payoutRule";
import { useAuth } from "../state/AuthContext";
import { useTheme, type ThemePreference } from "../theme/ThemeProvider";
import { ConfirmEmailNudge } from "../components/profile/ConfirmEmailNudge";
import { EditProfilePanel } from "../components/profile/EditProfilePanel";
import { PersonalAdjustmentsCard } from "../components/profile/PersonalAdjustmentsCard";
import { PersonalSalesCard } from "../components/profile/PersonalSalesCard";
import { PersonalShiftsCard } from "../components/profile/PersonalShiftsCard";
import { ProfileIdentityCard } from "../components/profile/ProfileIdentityCard";
import { VacationsCard } from "../components/profile/VacationsCard";

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Hidden from staff for now (owner, 2026-10-01); flip to true to bring it back.
const SHOW_PAY_ADJUSTMENTS = false;

export function ProfilePage() {
  const { user, logout, loading: logoutBusy, refreshUser } = useAuth();
  // Couriers only do deliveries: no shifts, no sales, no payroll adjustments,
  // no shop-floor PIN. The backend scope guard forbids those endpoints anyway.
  const isCourier = user?.role === "COURIER";
  // Housekeeping (G-103) has shifts, adjustments, a PIN and a language, but
  // never rings a sale — and the sales endpoint is closed to them by the same
  // scope guard, so asking would only render an error. (S4 moves their
  // shifts/adjustments to the My-work page and narrows this further.)
  const isHousekeeping = user?.role === "HOUSEKEEPING";
  const showsSales = !isCourier && !isHousekeeping;

  const [me, setMe] = useState<MeResponse | null>(null);
  const [meError, setMeError] = useState<string | null>(null);
  const [editingDetails, setEditingDetails] = useState(false);

  const [shifts, setShifts] = useState<Shift[]>([]);
  const [shiftsLoading, setShiftsLoading] = useState(!isCourier);
  const [shiftsError, setShiftsError] = useState<string | null>(null);
  const [earnings, setEarnings] = useState<MyWorkDayEarnings[] | null>(null);

  const [sales, setSales] = useState<Sale[]>([]);
  // G-169: my totals, added once by the server (refunds of my sales already off, G-162).
  const [salesTotals, setSalesTotals] = useState<SalePeriodTotals | null>(null);
  const [salesLoading, setSalesLoading] = useState(showsSales);
  const [salesError, setSalesError] = useState<string | null>(null);

  const now = new Date();
  const periodYear = now.getFullYear();
  const periodMonth = now.getMonth() + 1;

  const [adjustments, setAdjustments] = useState<PayrollAdjustmentsResponse | null>(null);
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(
    SHOW_PAY_ADJUSTMENTS && !isCourier,
  );
  const [adjustmentsError, setAdjustmentsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((res) => {
        if (!cancelled) setMe(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setMeError(err instanceof Error ? err.message : "Could not load profile");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isCourier) return;
    let cancelled = false;
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - 14);
    const to = new Date(today);
    to.setDate(today.getDate() + 14);
    setShiftsLoading(true);
    const range = { from: toIsoDate(from), to: toIsoDate(to) };
    // Earnings are a nicety on top of the list: if they fail, shifts still show.
    api
      .listMyEarnings(range)
      .then((rows) => {
        if (!cancelled) setEarnings(rows);
      })
      .catch(() => {
        if (!cancelled) setEarnings(null);
      });
    api
      .listMyShifts(range)
      .then((rows) => {
        if (!cancelled) setShifts(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setShiftsError(err instanceof Error ? err.message : "Could not load shifts");
        }
      })
      .finally(() => {
        if (!cancelled) setShiftsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isCourier]);

  useEffect(() => {
    if (!showsSales) return;
    let cancelled = false;
    setSalesLoading(true);
    Promise.all([api.listMySales({}), api.getMySalesTotals({})])
      .then(([rows, totals]) => {
        if (cancelled) return;
        setSales(rows);
        setSalesTotals(totals);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setSalesError(err instanceof Error ? err.message : "Could not load sales");
        }
      })
      .finally(() => {
        if (!cancelled) setSalesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showsSales]);

  useEffect(() => {
    if (!SHOW_PAY_ADJUSTMENTS || isCourier) return;
    let cancelled = false;
    setAdjustmentsLoading(true);
    api
      .listMyPayrollAdjustments({ year: periodYear, month: periodMonth })
      .then((answer) => {
        if (!cancelled) setAdjustments(answer);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setAdjustmentsError(
            err instanceof Error ? err.message : "Could not load adjustments",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setAdjustmentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [periodYear, periodMonth, isCourier]);

  if (!user) return null;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div>
        <h1 className="page-title">My profile</h1>
        <p className="page-subtitle">
          {isCourier
            ? "Your personal workspace: identity and security."
            : isHousekeeping
              ? "Your personal workspace: identity, security, and shifts."
              : "Your personal workspace: identity, security, shifts, and sales."}
        </p>
      </div>

      {meError ? <div className="alert error">{meError}</div> : null}
      {/* The app-wide banner steps aside on this page for the header's nudge;
          if the header could not load, the nudge still has to be somewhere. */}
      {meError && user.emailVerified === false ? (
        <div className="card profile-id-email-row">
          <span>{user.email}</span>
          <ConfirmEmailNudge />
        </div>
      ) : null}

      {me ? (
        <ProfileIdentityCard
          me={me}
          onEdit={() => setEditingDetails((open) => !open)}
          editing={editingDetails}
          showContract={!isCourier}
        />
      ) : (
        <div className="card muted">Loading your details…</div>
      )}

      {/* G-145: the pencil's panel — details, sign-in email, password. Couriers
          keep their own plane for name/phone, so theirs holds email and
          password only (they could always change those). */}
      {me && editingDetails ? (
        <EditProfilePanel
          me={me}
          canEditDetails={!isCourier}
          onDetailsSaved={async () => {
            setMe(await api.me());
            await refreshUser();
          }}
          onEmailChanged={async () => {
            setMe(await api.me());
          }}
          onClose={() => setEditingDetails(false)}
        />
      ) : null}

      {/* Couriers have no shop-floor PIN and no work-contract access here.
          Housekeeping (G-103) DO hold a PIN, but until S3 opens the kiosk
          presence door the tablet refuses it — so S1 does not show a card
          whose copy says "create one to clock in on the tablet". S3 lifts
          this gate together with the door. */}
      {me && !isCourier && !isHousekeeping ? <ProfileEmployeeAccessCard me={me} /> : null}

      {/* G-2 §16.6: a courier may see their OWN payout rule in full. */}
      {isCourier ? <CourierPayoutRateCard /> : null}

      {!isCourier ? (
        <PersonalShiftsCard
          shifts={shifts}
          earnings={earnings}
          loading={shiftsLoading}
          error={shiftsError}
          // Housekeeping has no shifts page until S4's My-work page exists.
          calendarPath={isHousekeeping ? null : "/app/my/shifts"}
        />
      ) : null}

      {/* G-109 (OD-78): cleaners request vacations. Everyone else does it on
          My shifts, which a cleaner cannot reach until S4's My-work page, so
          Profile carries it for them until then. */}
      {isHousekeeping ? <VacationsCard /> : null}

      {showsSales ? (
        <PersonalSalesCard
          sales={sales}
          totals={salesTotals}
          loading={salesLoading}
          error={salesError}
          role={user.role}
        />
      ) : null}

      {SHOW_PAY_ADJUSTMENTS && !isCourier ? (
        <PersonalAdjustmentsCard
          adjustments={adjustments}
          loading={adjustmentsLoading}
          error={adjustmentsError}
          periodYear={periodYear}
          periodMonth={periodMonth}
        />
      ) : null}

      {me && !isCourier ? (
        <LanguageCard me={me} onSaved={(next) => setMe(next)} />
      ) : null}

      <AppearanceCard />

      <div
        className="card"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "var(--space-3)",
          flexWrap: "wrap",
        }}
      >
        <div>
          <strong style={{ fontSize: 16 }}>Sign out</strong>
          <p className="muted" style={{ margin: "var(--space-1) 0 0", fontSize: 13 }}>
            Ends this session and clears your access on this device.
          </p>
        </div>
        <button
          type="button"
          className="button danger"
          onClick={() => void logout()}
          disabled={logoutBusy}
        >
          {logoutBusy ? "Signing out…" : "Sign out"}
        </button>
      </div>
    </section>
  );
}

/**
 * G-2 (Orders.md §16.6): the courier's own agreed payout rule, read-only and
 * in full — it is their contract. Past jobs keep their snapshotted payout
 * even if the rule changes later.
 */
function CourierPayoutRateCard() {
  const [view, setView] = useState<CourierPayoutRuleView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .courierMyPayoutRule()
      .then((res) => {
        if (!cancelled) setView(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Could not load your rate",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="card">
      <strong>My payout rate</strong>
      {error ? <p className="muted">{error}</p> : null}
      {!view && !error ? <p className="muted">Loading…</p> : null}
      {view ? (
        <>
          <p>
            {view.hasRule
              ? ruleSummary(view.rule)
              : "No rate agreed yet — each delivery gets a manual payout."}
          </p>
          <p className="muted">
            Every delivery snapshots its payout when it is assigned, so a later
            rate change never rewrites what you already earned.
          </p>
        </>
      ) : null}
    </div>
  );
}

/**
 * G-9.1: per-device theme preference. "System" follows the OS; the shared
 * floor tablet gets pinned by choosing "Light" on it. Stored in localStorage,
 * so each device keeps its own choice.
 */
function AppearanceCard() {
  const { preference, setPreference } = useTheme();
  const options: Array<{ value: ThemePreference; label: string }> = [
    { value: "light", label: "Light" },
    { value: "dark", label: "Dark" },
    { value: "system", label: "System" },
  ];
  return (
    <div className="card">
      <strong>Appearance</strong>
      <p className="muted">
        Theme for this device. “System” follows the device setting.
      </p>
      <div className="button-row">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={preference === option.value ? "button" : "button tinted"}
            aria-pressed={preference === option.value}
            onClick={() => setPreference(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * G-85 S6 — the language THIS PERSON is shown the app in (owner, 2026-09-16:
 * "person should be able to pick his default language in the profile").
 *
 * Unlike Appearance above, this is not a per-device setting: it is stored on
 * the person, so it follows them to any tablet they PIN into.
 *
 * "Default — English" is the resting option and it writes NOTHING (owner
 * ruling 2026-09-20: nobody is backfilled with `EN`). The distinction is real
 * and load-bearing: a stored `EN` means "always English, even on a counter
 * everyone else is running in Russian", while no row means "I don't mind —
 * use whatever this device is showing". Storing `EN` for everyone would make
 * the kiosk's tablet memory unreachable, because a person's own preference
 * outranks it.
 *
 * Today it governs kiosk mode; the CRM is still English everywhere until the
 * CRM i18n order lands, and the copy says so rather than over-promising.
 */
function LanguageCard({
  me,
  onSaved,
}: {
  me: MeResponse;
  onSaved: (next: MeResponse) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = me.language ?? "";

  const save = async (value: string) => {
    setSaving(true);
    setError(null);
    try {
      await api.patchMyLanguage(value === "" ? null : (value as AppLanguage));
      onSaved(await api.me());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save the language.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <strong>Language</strong>
      <p className="muted">
        The language kiosk mode opens in when you enter your PIN, on any
        tablet. Left on the default, the CRM is English and kiosk mode follows
        whatever the tablet is showing.
      </p>
      <label className="visually-hidden" htmlFor="profile-language">
        Language
      </label>
      <select
        id="profile-language"
        className="inventory-category-select"
        value={current}
        disabled={saving}
        onChange={(e) => void save(e.target.value)}
      >
        <option value="">Default — English</option>
        {APP_LANGUAGE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value} lang={option.lang}>
            {option.label}
          </option>
        ))}
      </select>
      {error ? <p className="alert error">{error}</p> : null}
    </div>
  );
}

function ProfileEmployeeAccessCard({ me }: { me: MeResponse }) {
  // PINs are server-generated and viewable (owner decision 2026-08-04) — a
  // florist sees their own PIN or requests a fresh one; they never type digits.
  const [pin, setPin] = useState<string | null>(null);
  const [pinLoading, setPinLoading] = useState(true);
  const [pinBusy, setPinBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    void shopModeApi
      .viewUserPin(me.id)
      .then((r) => {
        if (!stale) setPin(r.pin);
      })
      .catch(() => {
        /* leave pin null; the card still offers "create one" */
      })
      .finally(() => {
        if (!stale) setPinLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [me.id]);

  const regeneratePin = async () => {
    setMessage(null);
    setError(null);
    setPinBusy(true);
    try {
      const r = await shopModeApi.generateUserPin(me.id);
      setPin(r.pin);
      setMessage("New PIN created.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not create a PIN");
    } finally {
      setPinBusy(false);
    }
  };

  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <div>
        <strong style={{ fontSize: 16 }}>Employee access</strong>
        <p className="muted" style={{ margin: "var(--space-1) 0 0", fontSize: 13 }}>
          Your e-mail and job title are managed by your shop lead. Your shop-floor PIN
          is generated for you — view it here or request a new one.
        </p>
      </div>
      {message ? <div className="alert success">{message}</div> : null}
      {error ? <div className="alert error">{error}</div> : null}
      <div className="field">
        <label>Your shop PIN</label>
        {pinLoading ? (
          <p className="muted" style={{ margin: 0 }}>Loading…</p>
        ) : pin ? (
          <div className="pin-reveal">{pin}</div>
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            No PIN yet — create one to clock in on the tablet.
          </p>
        )}
      </div>
      <div className="button-row">
        <button
          type="button"
          className="button"
          onClick={() => void regeneratePin()}
          disabled={pinBusy}
        >
          {pinBusy ? "Creating…" : pin ? "Create a new PIN" : "Create PIN"}
        </button>
      </div>
    </div>
  );
}
